/**
 * League › History — champions, seasons, managers and records.
 *
 * It was a top-level section with seven tabs. Three of those have gone to the sections whose work
 * they describe (Tim, 22 August): past drafts to Draft, keeper history to Keepers, and the trade
 * archive nowhere, because League › Trades is the same object. What is left is the four screens
 * that are genuinely "the league's past" rather than "a reference you need while doing something",
 * and four tabs is a page rather than a section.
 *
 * Nothing below is rewritten. These are the same four components, standing in one place.
 */

import { useState } from 'react';
import { HistoryChampions } from './HistoryChampions.js';
import { HistorySeasons } from './HistorySeasons.js';
import { HistoryManagers } from './HistoryManagers.js';
import { League } from './League.js';
import type { LeagueStore, Role } from '../store/types.js';

type Tab = 'champions' | 'seasons' | 'managers' | 'records';

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'champions', label: 'Champions' },
  { id: 'seasons', label: 'Seasons' },
  { id: 'managers', label: 'Managers' },
  /*
   * Records is last and is not from the design, which never drew it. Careers, head-to-head and
   * every game played are answered nowhere else, so deleting it to match a drawing would lose the
   * answers rather than tidy them.
   */
  { id: 'records', label: 'Records' },
];

export interface HistoryHubProps {
  gm: string;
  actor: string;
  role: Role;
  store: LeagueStore;
  onGo: (section: string, page: string) => void;
}

export function HistoryHub({ gm, actor, role, store, onGo }: HistoryHubProps) {
  const [tab, setTab] = useState<Tab>('champions');
  return (
    <div className="app historyhub">
      {/*
        THESE ARE PAGES, NOT A FILTER (CD, 6 September). History is one app page with four
        screens inside it, so its tabs never reached the app's sub-nav and were drawn as a
        segmented control — the language the app uses for "which slice of this page", which is
        not the question these four answer. Same component, same four tabs, same state; the row
        is the sub-nav now, with the section named in front of it like every other one.
      */}
      <div className="subnav inpage">
        <span className="subwhere" aria-hidden="true"><i />History<b>/</b></span>
        <div className="subtabs" role="tablist" aria-label="History views">
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
              className={`subtab ${tab === t.id ? 'on' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'champions' && <HistoryChampions gm={gm} />}
      {tab === 'seasons' && <HistorySeasons onGo={onGo} />}
      {tab === 'managers' && <HistoryManagers gm={actor} role={role} store={store} />}
      {tab === 'records' && (
        <League view="records" gm={gm} actor={actor} role={role} store={store} />
      )}
    </div>
  );
}
